/**
 * Afín 4×4 Volume index ↔ Patient (LPS / World DICOM).
 * Row-major: p_patient = M * [i, j, k, 1]^T
 */

/** @typedef {number[]} Mat4 */

function asVec3(arr) {
  if (!arr || arr.length < 3) return null
  return [Number(arr[0]), Number(arr[1]), Number(arr[2])]
}

function len3(a) {
  return Math.sqrt(a[0] * a[0] + a[1] * a[1] + a[2] * a[2])
}

function normalize3(a) {
  const L = len3(a)
  if (!L) return [0, 0, 0]
  return [a[0] / L, a[1] / L, a[2] / L]
}

function cross3(a, b) {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ]
}

/**
 * @returns {Mat4}
 */
export function identityMat4() {
  return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]
}

/**
 * @param {Mat4|null|undefined} m
 * @returns {Mat4}
 */
export function cloneMat4(m) {
  if (!m || m.length < 16) return identityMat4()
  return Array.prototype.slice.call(m, 0, 16).map((n) => Number(n) || 0)
}

/**
 * Multiplica Mat4 × Mat4 (row-major).
 * @param {Mat4} a
 * @param {Mat4} b
 * @returns {Mat4}
 */
export function mulMat4(a, b) {
  const o = new Array(16)
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) {
      o[r * 4 + c] =
        a[r * 4 + 0] * b[0 * 4 + c] +
        a[r * 4 + 1] * b[1 * 4 + c] +
        a[r * 4 + 2] * b[2 * 4 + c] +
        a[r * 4 + 3] * b[3 * 4 + c]
    }
  }
  return o
}

/**
 * Punto (o vector homogéneo w=1) por Mat4.
 * @param {Mat4} m
 * @param {{x:number,y:number,z:number}|number[]} p
 * @returns {[number, number, number]}
 */
export function transformPointMat4(m, p) {
  const x = Array.isArray(p) ? Number(p[0]) || 0 : Number(p.x) || 0
  const y = Array.isArray(p) ? Number(p[1]) || 0 : Number(p.y) || 0
  const z = Array.isArray(p) ? Number(p[2]) || 0 : Number(p.z) || 0
  const X = m[0] * x + m[1] * y + m[2] * z + m[3]
  const Y = m[4] * x + m[5] * y + m[6] * z + m[7]
  const Z = m[8] * x + m[9] * y + m[10] * z + m[11]
  const W = m[12] * x + m[13] * y + m[14] * z + m[15]
  if (W && Math.abs(W - 1) > 1e-9) {
    return [X / W, Y / W, Z / W]
  }
  return [X, Y, Z]
}

/**
 * Solo parte lineal 3×3 (direcciones / deltas de índice).
 * @param {Mat4} m
 * @param {number[]} d
 * @returns {[number, number, number]}
 */
export function transformDirMat4(m, d) {
  const x = Number(d[0]) || 0
  const y = Number(d[1]) || 0
  const z = Number(d[2]) || 0
  return [
    m[0] * x + m[1] * y + m[2] * z,
    m[4] * x + m[5] * y + m[6] * z,
    m[8] * x + m[9] * y + m[10] * z,
  ]
}

/**
 * Inversa de afín 4×4 (asume última fila [0,0,0,1] y 3×3 invertible).
 * @param {Mat4} m
 * @returns {Mat4|null}
 */
export function invertMat4Affine(m) {
  // 3×3 superior
  const a00 = m[0]
  const a01 = m[1]
  const a02 = m[2]
  const a10 = m[4]
  const a11 = m[5]
  const a12 = m[6]
  const a20 = m[8]
  const a21 = m[9]
  const a22 = m[10]
  const det =
    a00 * (a11 * a22 - a12 * a21) -
    a01 * (a10 * a22 - a12 * a20) +
    a02 * (a10 * a21 - a11 * a20)
  if (!Number.isFinite(det) || Math.abs(det) < 1e-12) return null
  const invDet = 1 / det
  const b00 = (a11 * a22 - a12 * a21) * invDet
  const b01 = (a02 * a21 - a01 * a22) * invDet
  const b02 = (a01 * a12 - a02 * a11) * invDet
  const b10 = (a12 * a20 - a10 * a22) * invDet
  const b11 = (a00 * a22 - a02 * a20) * invDet
  const b12 = (a02 * a10 - a00 * a12) * invDet
  const b20 = (a10 * a21 - a11 * a20) * invDet
  const b21 = (a01 * a20 - a00 * a21) * invDet
  const b22 = (a00 * a11 - a01 * a10) * invDet
  const tx = m[3]
  const ty = m[7]
  const tz = m[11]
  return [
    b00,
    b01,
    b02,
    -(b00 * tx + b01 * ty + b02 * tz),
    b10,
    b11,
    b12,
    -(b10 * tx + b11 * ty + b12 * tz),
    b20,
    b21,
    b22,
    -(b20 * tx + b21 * ty + b22 * tz),
    0,
    0,
    0,
    1,
  ]
}

/**
 * Construye index→patient a partir de ejes unitarios, spacing y origen IPP.
 * @param {{
 *   ex: number[], ey: number[], ez: number[],
 *   sx: number, sy: number, sz: number,
 *   origin: number[],
 * }} args
 * @returns {{ indexToPatient: Mat4, patientToIndex: Mat4|null, spacing: object, axes: object, origin: number[] }}
 */
export function buildVolumeAffine(args) {
  const ex = normalize3(args.ex || [1, 0, 0])
  const ey = normalize3(args.ey || [0, 1, 0])
  const ez = normalize3(args.ez || [0, 0, 1])
  const sx = Number(args.sx) > 0 ? Number(args.sx) : 1
  const sy = Number(args.sy) > 0 ? Number(args.sy) : 1
  const sz = Number(args.sz) > 0 ? Number(args.sz) : 1
  const origin = asVec3(args.origin) || [0, 0, 0]
  const indexToPatient = [
    ex[0] * sx,
    ey[0] * sy,
    ez[0] * sz,
    origin[0],
    ex[1] * sx,
    ey[1] * sy,
    ez[1] * sz,
    origin[1],
    ex[2] * sx,
    ey[2] * sy,
    ez[2] * sz,
    origin[2],
    0,
    0,
    0,
    1,
  ]
  const patientToIndex = invertMat4Affine(indexToPatient)
  return {
    indexToPatient,
    patientToIndex,
    spacing: { sx, sy, sz },
    axes: { ex, ey, ez },
    origin,
  }
}

/**
 * Pixel (col,row) del plano resliced → paciente LPS.
 * center = índice de volumen del centro del plano; u/v en espacio de índices.
 * @param {Mat4} indexToPatient
 * @param {{ u: number[], v: number[] }} frame
 * @param {{ outCols: number, outRows: number }} out
 * @param {{ x: number, y: number }} pixel
 * @param {{ x: number, y: number, z: number }} center
 * @returns {[number, number, number]}
 */
export function planePixelToPatient(
  indexToPatient,
  frame,
  out,
  pixel,
  center
) {
  const oc = Math.max(1, Number(out && out.outCols) || 1)
  const or_ = Math.max(1, Number(out && out.outRows) || 1)
  const cx = (oc - 1) / 2
  const cy = (or_ - 1) / 2
  const dc = (Number(pixel && pixel.x) || 0) - cx
  const dr = (Number(pixel && pixel.y) || 0) - cy
  const u = frame.u || [1, 0, 0]
  const v = frame.v || [0, 1, 0]
  const ix =
    (Number(center && center.x) || 0) + dc * u[0] + dr * v[0]
  const iy =
    (Number(center && center.y) || 0) + dc * u[1] + dr * v[1]
  const iz =
    (Number(center && center.z) || 0) + dc * u[2] + dr * v[2]
  return transformPointMat4(indexToPatient, [ix, iy, iz])
}

/**
 * Paciente LPS → pixel (col,row) del plano (proyección al plano por n).
 * @returns {{ x: number, y: number, alongNormal: number }|null}
 */
export function patientToPlanePixel(
  patientToIndex,
  frame,
  out,
  patient,
  center
) {
  if (!patientToIndex) return null
  const idx = transformPointMat4(patientToIndex, patient)
  const u = frame.u || [1, 0, 0]
  const v = frame.v || [0, 1, 0]
  const n = frame.n || cross3(u, v)
  const dx = idx[0] - (Number(center && center.x) || 0)
  const dy = idx[1] - (Number(center && center.y) || 0)
  const dz = idx[2] - (Number(center && center.z) || 0)
  const alongU = dx * u[0] + dy * u[1] + dz * u[2]
  const alongV = dx * v[0] + dy * v[1] + dz * v[2]
  const alongN = dx * n[0] + dy * n[1] + dz * n[2]
  const oc = Math.max(1, Number(out && out.outCols) || 1)
  const or_ = Math.max(1, Number(out && out.outRows) || 1)
  return {
    x: (oc - 1) / 2 + alongU,
    y: (or_ - 1) / 2 + alongV,
    alongNormal: alongN,
  }
}

/**
 * Distancia euclídea paciente (mm) entre dos píxeles del mismo plano.
 * @param {Mat4} indexToPatient
 * @param {{ u: number[], v: number[] }} frame
 * @param {{ outCols: number, outRows: number }} out
 * @param {{ x: number, y: number }} a
 * @param {{ x: number, y: number }} b
 * @param {{ x: number, y: number, z: number }} center
 */
export function planePixelDistanceMm(
  indexToPatient,
  frame,
  out,
  a,
  b,
  center
) {
  const pa = planePixelToPatient(indexToPatient, frame, out, a, center)
  const pb = planePixelToPatient(indexToPatient, frame, out, b, center)
  const dx = pb[0] - pa[0]
  const dy = pb[1] - pa[1]
  const dz = pb[2] - pa[2]
  return Math.sqrt(dx * dx + dy * dy + dz * dz)
}
